/**
 * "The open row for this vehicle": a reference field that starts with the
 * latest row of the record it points at whose status is open and whose
 * reference equals this entry's (step 9.2 of docs/plans/ledger.md, D5 of the
 * Phase 9 brief).
 *
 * A fuel entry picks its Vehicle, and its Trip starts with the trip that
 * vehicle is running. This is what the Link target `match_open_row` was meant
 * to do, said as a default instead: the person sees the trip on the form and
 * may pick another, and nothing is written behind their back.
 *
 * It lives in `app.field.default_expr` under `openRow`, beside `formula` (a
 * Starts with) and `showWhen`, in the `{from_reference, field}` shape that
 * column has always described:
 *
 *   { openRow: { from_reference: <a reference field on THIS record>,
 *                field: <the field on the TARGET record that holds the same reference>,
 *                status?: { field: <a select on the target>, open: [<choice ids or keys>] } } }
 *
 * The form does the filling, as it does for every default: the server answers
 * "which row is open for this vehicle" (`GET /v1/records/:recordId/fields/:fieldId/open-row`)
 * and the write path never applies it.
 */
/** The default as every reader holds it. Ids on a live field, keys in a spec. */
export type OpenRowDefault = {
  /** A reference field on THIS record: the Vehicle of the fuel entry. */
  readonly by: string;
  /** The field on the record this field points at that holds the same reference: the Trip's Vehicle. */
  readonly match: string;
  /**
   * The select on the target that says a row is open, and the choices that
   * mean open: Trip status is Running. Absent: the latest matching row is the
   * open one.
   */
  readonly status?: { readonly field: string; readonly open: readonly string[] };
};

/** The key `app.field.default_expr` holds it under. */
export const OPEN_ROW_KEY = 'openRow';

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const isName = (value: unknown): value is string => typeof value === 'string' && value.trim() !== '';

/**
 * The stored document (`{from_reference, field, status?}`), or the reader's
 * shape (`{by, match, status?}`), checked without trusting the caller. Null on
 * anything else: a default that does not parse fills nothing.
 */
export const readOpenRow = (raw: unknown): OpenRowDefault | null => {
  if (!isObject(raw)) return null;
  const by = raw.from_reference ?? raw.by;
  const match = raw.field ?? raw.match;
  if (!isName(by) || !isName(match)) return null;
  if (raw.status === undefined || raw.status === null) return { by, match };
  if (!isObject(raw.status) || !isName(raw.status.field) || !Array.isArray(raw.status.open)) return null;
  const open = raw.status.open.filter(isName);
  if (open.length === 0 || open.length !== raw.status.open.length) return null;
  return { by, match, status: { field: raw.status.field, open } };
};

/** The document `default_expr.openRow` stores. */
export const openRowDocument = (rule: OpenRowDefault): Record<string, unknown> => ({
  from_reference: rule.by,
  field: rule.match,
  ...(rule.status === undefined ? {} : { status: { field: rule.status.field, open: [...rule.status.open] } }),
});

/** One field as the check needs it. */
export type OpenRowField = {
  readonly id: string;
  readonly type: string;
  /** The record a reference points at, by id or key. */
  readonly target?: string | null;
};

/**
 * Every problem with an open-row default, as sentences a setup or a person
 * reads, or none. `field` is the field that carries it, `own` the fields of
 * its record, `targetFields` the fields of the record `field` points at.
 */
export const openRowProblems = (
  rule: OpenRowDefault,
  field: OpenRowField,
  own: readonly OpenRowField[],
  targetFields: readonly OpenRowField[],
): string[] => {
  const problems: string[] = [];
  if (field.type !== 'reference' || field.target === undefined || field.target === null) {
    problems.push('Only a field that picks one row starts with the open row.');
    return problems;
  }
  const by = own.find((one) => one.id === rule.by);
  if (by === undefined || by.type !== 'reference') problems.push(`"${rule.by}" is not a field that picks one row on this record.`);
  if (rule.by === field.id) problems.push('The open row is found by another field, never by the field itself.');
  const match = targetFields.find((one) => one.id === rule.match);
  if (match === undefined || match.type !== 'reference') {
    problems.push(`"${rule.match}" is not a field that picks one row on the record "${field.id}" points at.`);
  } else if (by !== undefined && by.target !== match.target) {
    problems.push(`"${rule.by}" and "${rule.match}" pick from different lists, so no row can match.`);
  }
  if (rule.status !== undefined) {
    const status = targetFields.find((one) => one.id === rule.status!.field);
    if (status === undefined || status.type !== 'select') problems.push(`"${rule.status.field}" is not a choice field on the record "${field.id}" points at.`);
  }
  return problems;
};

/** One row of the target record, as the pick reads it. */
export type OpenRowCandidate = {
  readonly entryId: string;
  /** The row's value of `match`: the vehicle it is for. */
  readonly match: unknown;
  /** The row's value of the status field, when the rule has one: a choice id. */
  readonly status?: unknown;
  /** When the row was made, ISO; the latest one is the open row. */
  readonly createdAt: string;
};

/**
 * The open row among the target's rows for this entry's `by` value, or null.
 * The rule the server's query runs, kept here so a device holding the rows can
 * run it the same way. `openStatus` holds every choice id and key that means open.
 */
export const pickOpenRow = (
  rows: readonly OpenRowCandidate[],
  byValue: unknown,
  rule: OpenRowDefault,
  openStatus: ReadonlySet<string> = new Set(rule.status?.open ?? []),
): string | null => {
  if (typeof byValue !== 'string' || byValue === '') return null;
  let best: OpenRowCandidate | null = null;
  for (const row of rows) {
    if (typeof row.match !== 'string' || row.match.toLowerCase() !== byValue.toLowerCase()) continue;
    if (rule.status !== undefined && (typeof row.status !== 'string' || !openStatus.has(row.status))) continue;
    if (best === null || row.createdAt > best.createdAt) best = row;
  }
  return best?.entryId ?? null;
};

/**
 * `GET /v1/records/:recordId/fields/:fieldId/open-row?by=<row id>`: the open
 * row the field starts with for that `by`, with its title so the picker can
 * draw it, or null when no row is open for it (or the person may not see it).
 */
export type OpenRowResponse = { readonly row: { readonly id: string; readonly title: string } | null };
